import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classes from "./HomePage.module.css";
import { useDispatch, useSelector } from 'react-redux';
import { Calendar } from "primereact/calendar";
import moment from "moment-timezone";
import { DataTable } from "primereact/datatable";
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { liveMisApi, misApi } from '../http/http';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { removeToken } from '../store/slices/tokenSlice';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
    const { tokenData } = useSelector(state => state?.tokenSlice);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);

    const [data, setData] = useState([]);
    const [liveData, setLiveData] = useState([]);
    const [monthYear, setMonthYear] = useState(new Date());
    const [showTodayMis, setShowTodayMis] = useState(true);

    const handleMonthYearChange = (e) => {
        setMonthYear(e.value);
    };

    const fetchLiveMis = useCallback(async () => {
        try {
            const response = await liveMisApi(tokenData);
            setLoading(false);

            setLiveData(response?.data?.result);

        } catch (error) {
            console.log(error, 'error');
            setLoading(false);

            if (error?.response?.status === 403) {
                dispatch(removeToken());
                toast.error(t('common.tokenExpired'));
                navigate('/');
            }
            else {
                toast.error(error?.response?.data?.error?.error || error?.response?.error?.error || error?.response?.error || t('common.somethingWentWrong'));
            }
        }
    }, [dispatch, navigate, t, tokenData]);

    const fetchMis = useCallback(async () => {
        try {
            setLoading(true);

            const startDate = moment(monthYear).startOf("month").format("YYYY-MM-DD");
            const endDate = moment(monthYear).endOf("month").format("YYYY-MM-DD");

            // const response = await misApi(dates.from, dates.to, tokenData);
            const response = await misApi(startDate, endDate, tokenData);
            const response2 = response?.data?.result?.filter((item) => {
                return moment(item?.mis_date).isSameOrBefore(new Date());
            }) ?? [];
            setData(response2.reverse());
            fetchLiveMis();

            // const total_subscriptions_sum = response?.data?.result.reduce(
            //     (total, dataItem) => total + Number(dataItem.total_subscriptions ?? 0),
            //     0
            // );

            // const total_unsubscriptions_sum = response?.data?.result.reduce(
            //     (total, dataItem) => total + Number(dataItem.total_unsubscriptions ?? 0),
            //     0
            // );

            // const subscription_revenue_sum = response?.data?.result.reduce(
            //     (total, dataItem) => total + Number(dataItem.subscription_revenue ?? 0),
            //     0
            // );

            // const renewal_revenue_sum = response?.data?.result.reduce(
            //     (total, dataItem) => total + Number(dataItem.renewal_revenue ?? 0),
            //     0
            // );

            // const total_revenue_sum = response?.data?.result.reduce(
            //     (total, dataItem) => total + Number(dataItem.total_revenue ?? 0),
            //     0
            // );

            // setTotal({
            //     mis_date: "Totals",
            //     total_base: "",
            //     total_subscriptions: total_subscriptions_sum.toFixed(2),
            //     total_unsubscriptions: total_unsubscriptions_sum.toFixed(2),
            //     subscription_revenue: subscription_revenue_sum.toFixed(2),
            //     renewal_count: "",
            //     renewal_revenue: renewal_revenue_sum.toFixed(2),
            //     total_revenue: total_revenue_sum.toFixed(2),
            // });


        } catch (error) {
            console.log(error, 'error');
            setLoading(false);

            if (error?.response?.status === 403) {
                dispatch(removeToken());
                toast.error(t('common.tokenExpired'));
                navigate('/');
            }
            else {
                toast.error(error?.response?.data?.error?.error || error?.response?.error?.error || error?.response?.error || t('common.somethingWentWrong'));
            }
        }
    }, [dispatch, fetchLiveMis, monthYear, navigate, t, tokenData]);

    useEffect(() => {
        fetchMis();
    }, [fetchMis]);

    const tableData = useMemo(() => {
        const liveRows = Array.isArray(liveData)
            ? liveData
            : liveData && Object.keys(liveData).length ? [liveData] : [];
        return [...liveRows, ...data];
    }, [liveData, data]);

    const formatValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }

        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue.toLocaleString("en-US") : value;
    };

    const latestRow = tableData[0] ?? {};
    const getRenewalDaily = (rowData) => rowData?.renewal_web_daily ?? rowData?.ren_web_daily;
    const getRenewalWeekly = (rowData) => rowData?.renewal_web_weekly ?? rowData?.ren_web_weekly;

    const summaryCards = [
        {
            key: "totalBase",
            label: t('reports.totalActiveBase'),
            value: latestRow?.total_active_base,
            tone: classes.blueCard,
            details: [
                { label: t('reports.total'), value: latestRow?.total_active_base },
            ],
        },
        {
            key: "subscription",
            label: t('reports.subscription'),
            value: latestRow?.total_subscriptions,
            tone: classes.greenCard,
            details: [
                { label: t('reports.daily'), channel: t('reports.web'), value: latestRow?.sub_web_daily },
                { label: t('reports.weekly'), channel: t('reports.web'), value: latestRow?.sub_web_weekly },
                { label: t('reports.total'), channel: t('reports.web'), value: latestRow?.total_subscriptions },
            ],
        },
        {
            key: "unsubscription",
            label: t('reports.unsubscription'),
            value: latestRow?.total_unsubscriptions,
            tone: classes.redCard,
            details: [
                { label: t('reports.daily'), channel: t('reports.web'), value: latestRow?.unsub_web_daily },
                { label: t('reports.weekly'), channel: t('reports.web'), value: latestRow?.unsub_web_weekly },
                { label: t('reports.total'), channel: t('reports.web'), value: latestRow?.total_unsubscriptions },
            ],
        },
        {
            key: "charged",
            label: t('reports.charged'),
            value: latestRow?.total_sub_charged,
            tone: classes.purpleCard,
            details: [
                { label: t('reports.daily'), channel: t('reports.web'), value: latestRow?.sub_charged_web_daily },
                { label: t('reports.weekly'), channel: t('reports.web'), value: latestRow?.sub_charged_web_weekly },
                { label: t('reports.total'), channel: t('reports.web'), value: latestRow?.total_sub_charged },
            ],
        },
        {
            key: "renewals",
            label: t('reports.renewals'),
            value: latestRow?.total_renewals,
            tone: classes.cyanCard,
            details: [
                { label: t('reports.daily'), channel: t('reports.web'), value: getRenewalDaily(latestRow) },
                { label: t('reports.weekly'), channel: t('reports.web'), value: getRenewalWeekly(latestRow) },
                { label: t('reports.total'), channel: t('reports.web'), value: latestRow?.total_renewals },
            ],
        },
    ];

    const revenueCard = {
        label: t('reports.revenue'),
        value: latestRow?.total_revenue,
        details: [
            { label: t('reports.subscriptions'), value: latestRow?.total_sub_revenue },
            { label: t('reports.renewals'), value: latestRow?.total_renewals_revenue },
            { label: t('reports.total'), value: latestRow?.total_revenue },
            { label: t('reports.accumulated'), value: latestRow?.total_accumulated_revenue },
        ],
    };

    const renderSummaryCard = (card) => (
        <article className={`${classes.summary_card} ${card.tone}`} key={card.label}>
            <div className={classes.card_head}>
                <span>{card.label}</span>
                <strong>{formatValue(card.value)}</strong>
            </div>
            <div className={classes.card_details}>
                {/* {card.details.map((item) => (
                    <div className={classes.card_detail} key={`${card.label}-${item.label}`}>
                        <div className={classes.detail_label}>
                            <span>{item.label}</span>
                            {item.channel && <small>{item.channel}</small>}
                        </div>
                        <strong>{formatValue(item.value)}</strong>
                    </div>
                ))} */}
            </div>
        </article>
    );

    const renderRevenueCard = () => (
        <article className={classes.revenue_panel}>
            <div className={classes.revenue_head}>
                <span>{revenueCard.label}</span>
                <strong>{formatValue(revenueCard.value)}</strong>
            </div>
            <div className={classes.revenue_details}>
                {/* {revenueCard.details.map((item) => (
                    <div className={classes.revenue_detail} key={`${revenueCard.label}-${item.label}`}>
                        <span>{item.label}</span>
                        <strong>{formatValue(item.value)}</strong>
                    </div>
                ))} */}
            </div>
        </article>
    );

    const valueBody = (field) => (rowData) => formatValue(rowData?.[field]);
    const renewalDailyBody = (rowData) => formatValue(getRenewalDaily(rowData));
    const renewalWeeklyBody = (rowData) => formatValue(getRenewalWeekly(rowData));
    const dateBody = (rowData) => {
        const date = rowData?.mis_date_time ?? rowData?.mis_date;
        return date ? moment(date).format("DD-MM-YYYY") : "-";
    };

    const headerGroup = (
        <ColumnGroup>
            {/* Row 1: Main Groups */}
            <Row>
                <Column alignHeader="center"header={t('common.date')} rowSpan={3} />
                <Column alignHeader="center"header={t('reports.totalActiveBase')} rowSpan={3} />

                <Column alignHeader="center"header={t('reports.unsubscription')} colSpan={3} />
                <Column alignHeader="center"header={t('reports.subscription')} colSpan={3} />
                <Column alignHeader="center"header={t('reports.charged')} colSpan={3} />
                <Column alignHeader="center"header={t('reports.renewals')} colSpan={3} />
                <Column alignHeader="center"header={t('reports.revenue')} colSpan={4} rowSpan={2} />
            </Row>

            {/* Row 2: Subgroups WEB / USSD */}
            <Row>
                <Column alignHeader="center"header={t('reports.web')} colSpan={2} />
                {/* <Column alignHeader="center"header={t('reports.ussd')} colSpan={2} /> */}
                <Column alignHeader="center"header={t('reports.total')} rowSpan={2} />
                <Column alignHeader="center"header={t('reports.web')} colSpan={2} />
                {/* <Column alignHeader="center"header={t('reports.ussd')} colSpan={2} /> */}
                <Column alignHeader="center"header={t('reports.total')}  rowSpan={2} />
                <Column alignHeader="center"header={t('reports.web')} colSpan={2} />
                {/* <Column alignHeader="center"header={t('reports.ussd')} colSpan={2} /> */}
                <Column alignHeader="center"header={t('reports.total')}  rowSpan={2} />
                <Column alignHeader="center"header={t('reports.web')} colSpan={2} />
                {/* <Column alignHeader="center"header={t('reports.ussd')} colSpan={2} /> */}
                <Column alignHeader="center"header={t('reports.total')}  rowSpan={2} />
            </Row>

            {/* Row 3: Periods */}
            <Row>
                {/* Unsub - WEB & USSD */}
                <Column alignHeader="center"header={t('reports.daily')} />
                <Column alignHeader="center"header={t('reports.weekly')} />
                {/* <Column alignHeader="center"header={t('reports.daily')} /> */}
                {/* <Column alignHeader="center"header={t('reports.weekly')} /> */}

                {/* Sub - WEB & USSD */}
                <Column alignHeader="center"header={t('reports.daily')} />
                <Column alignHeader="center"header={t('reports.weekly')} />
                {/* <Column alignHeader="center"header={t('reports.daily')} /> */}
                {/* <Column alignHeader="center"header={t('reports.weekly')} /> */}

                {/* Charged - WEB & USSD */}
                <Column alignHeader="center"header={t('reports.daily')} />
                <Column alignHeader="center"header={t('reports.weekly')} />
                {/* <Column alignHeader="center"header={t('reports.daily')} /> */}
                {/* <Column alignHeader="center"header={t('reports.weekly')} /> */}

                {/* Renewals - WEB & USSD */}
                <Column alignHeader="center"header={t('reports.daily')} />
                <Column alignHeader="center"header={t('reports.weekly')} />
                {/* <Column alignHeader="center"header={t('reports.daily')} /> */}
                {/* <Column alignHeader="center"header={t('reports.weekly')} /> */}


                   {/* Revenue  */}
                <Column alignHeader="center"header={t('reports.subscriptions')} />
                <Column alignHeader="center"header={t('reports.renewals')} />
                <Column alignHeader="center"header={t('reports.total')} />
                <Column alignHeader="center"header={t('reports.accumulated')} />
            </Row>
        </ColumnGroup>
    );

    return (
        <>
            <Layout active={0}>

                <section className={classes.toolbar}>
                    <div>
                        <p className={classes.eyebrow}>{t('reports.revenueReport')}</p>
                        <h1 className={classes.page_title}>{t('nav.mis')}</h1>
                    </div>
                    <div className={classes.date_container}>
                        {/* <Calendar
                            value={startDateForCalendar}
                            onChange={(e) => convertStartDate(e.value)}
                            showIcon
                            showButtonBar
                            placeholder="Start Date"
                            style={{ width: "100%" }}
                            maxDate={endDateForCalendar}
                        /> */}

                        <Calendar
                            showIcon
                            showButtonBar
                            placeholder={t('form.monthYear')}
                            style={{ width: "100%" }}
                            view="month"
                            dateFormat="mm/yy"
                            value={monthYear}
                            onChange={handleMonthYearChange}
                            maxDate={new Date()}
                        />
                    </div>
                    {/* <div className={classes.date_container}>
                        <Calendar
                            value={endDateForCalendar}
                            onChange={(e) => convertEndDate(e.value)}
                            showIcon
                            // touchUI
                            showButtonBar
                            placeholder="End Date"
                            style={{ width: "100%" }}
                            minDate={startDateForCalendar}
                            maxDate={new Date()}
                        />
                    </div> */}

                    {/* <Button label='Search' onClick={fetchMis} /> */}

                </section>

                <div className={classes.message_container}>
                    {/* <i className="pi pi-info-circle" style={{color:"#1F41BA",fontSize:"18px"}} /> */}
                    <p className={classes.message_title}>{t('reports.revenueReport')}</p>
                </div>

                {loading ? <Spinner /> : (
                    <>
                    <section className={classes.summary_section}>
                        <div className={classes.summary_toolbar}>
                            <button
                                className={`${classes.today_mis_button} ${showTodayMis ? classes.today_mis_button_open : ""}`}
                                type="button"
                                onClick={() => setShowTodayMis((prev) => !prev)}
                                aria-expanded={showTodayMis}
                            >
                                Today MIS
                                <span className={classes.today_mis_icon}>{showTodayMis ? "^" : "v"}</span>
                            </button>
                        </div>

                        {showTodayMis && (
                            <div className={classes.summary_grid}>
                                {summaryCards.map((card) => renderSummaryCard(card))}
                                {renderRevenueCard()}
                            </div>
                        )}
                    </section>

                    <div className={classes.table_container}>

                        <DataTable
                            value={tableData}
                            headerColumnGroup={headerGroup}
                            emptyMessage={t('reports.noDataFound')}
                            showGridlines
                            scrollable
                            scrollDirection="both"
                            scrollHeight="clamp(360px, 62vh, 680px)"
                            // responsiveLayout="scroll"
                            paginator
                            rows={40}
                            rowsPerPageOptions={[10, 20, 40, 80]}
                            stripedRows
                            removableSort
                            size="small"
                            className={classes.report_table}
                        >
                            {/* Basic */}
                            <Column alignHeader="center"field="mis_date_time" body={dateBody} frozen style={{ minWidth: "128px" }} />
                            <Column alignHeader="center"field="total_active_base" body={valueBody("total_active_base")} style={{ minWidth: "128px" }} />

                            {/* Unsub */}
                            <Column alignHeader="center"field="unsub_web_daily" body={valueBody("unsub_web_daily")} style={{ minWidth: "112px" }} />
                            <Column alignHeader="center"field="unsub_web_weekly" body={valueBody("unsub_web_weekly")} style={{ minWidth: "112px" }} />
                            {/* <Column alignHeader="center"field="unsub_ussd_daily" /> */}
                            {/* <Column alignHeader="center"field="unsub_ussd_weekly" /> */}
                            <Column alignHeader="center"field="total_unsubscriptions" body={valueBody("total_unsubscriptions")} style={{ minWidth: "128px" }} />

                            {/* Sub */}
                            <Column alignHeader="center"field="sub_web_daily" body={valueBody("sub_web_daily")} style={{ minWidth: "112px" }} />
                            <Column alignHeader="center"field="sub_web_weekly" body={valueBody("sub_web_weekly")} style={{ minWidth: "112px" }} />
                            {/* <Column alignHeader="center"field="sub_ussd_daily" /> */}
                            {/* <Column alignHeader="center"field="sub_ussd_weekly" /> */}
                            <Column alignHeader="center"field="total_subscriptions" body={valueBody("total_subscriptions")} style={{ minWidth: "128px" }} />

                            {/* Charged */}
                            <Column alignHeader="center"field="sub_charged_web_daily" body={valueBody("sub_charged_web_daily")} style={{ minWidth: "112px" }} />
                            <Column alignHeader="center"field="sub_charged_web_weekly" body={valueBody("sub_charged_web_weekly")} style={{ minWidth: "112px" }} />
                            {/* <Column alignHeader="center"field="sub_charged_ussd_daily" /> */}
                            {/* <Column alignHeader="center"field="sub_charged_ussd_weekly" /> */}
                            <Column alignHeader="center"field="total_sub_charged" body={valueBody("total_sub_charged")} style={{ minWidth: "128px" }} />

                            {/* Renewals */}
                            <Column alignHeader="center"field="renewal_web_daily" body={renewalDailyBody} style={{ minWidth: "112px" }} />
                            <Column alignHeader="center"field="renewal_web_weekly" body={renewalWeeklyBody} style={{ minWidth: "112px" }} />
                            {/* <Column alignHeader="center"field="renewal_ussd_daily" body={(rowData) => rowData?.renewal_ussd_daily ?? rowData?.ren_ussd_daily} /> */}
                            {/* <Column alignHeader="center"field="renewal_ussd_weekly" body={(rowData) => rowData?.renewal_ussd_weekly ?? rowData?.ren_ussd_weekly} /> */}
                            <Column alignHeader="center"field="total_renewals" body={valueBody("total_renewals")} style={{ minWidth: "128px" }} />

                            {/* Revenue */}
                            <Column alignHeader="center"field="total_sub_revenue" body={valueBody("total_sub_revenue")} style={{ minWidth: "132px" }} />
                            <Column alignHeader="center"field="total_renewals_revenue" body={valueBody("total_renewals_revenue")} style={{ minWidth: "132px" }} />
                            <Column alignHeader="center"field="total_revenue" body={valueBody("total_revenue")} style={{ minWidth: "132px" }} />
                            <Column alignHeader="center"field="total_accumulated_revenue" body={valueBody("total_accumulated_revenue")} style={{ minWidth: "152px" }} />
                        </DataTable>

                    </div>
                    </>
                )}


            </Layout>

        </>

    )
}

export default HomePage
